// src/Components/Broadcast/components/PreviewModal.js
import React from 'react';
import { Eye, ImageIcon, MessageSquare, Loader, Send, X } from 'lucide-react';

export const PreviewModal = ({
  showPreview,
  setShowPreview,
  previewData,
  targetFilter,
  sending,
  sendProgress,
  sendMessage
}) => {
  if (!showPreview) return null;

  const getFilterName = () => {
    switch (targetFilter) {
      case 'all': return 'Все пользователи';
      case 'premium': return 'Premium';
      case 'free': return 'Без Premium';
      case 'selected': return 'Выбранные';
      default: return 'Все пользователи';
    }
  };

  const getTypeName = () => {
    switch (previewData?.type) {
      case 'text': return 'Текстовое';
      case 'notification': return 'Уведомление';
      case 'alert': return 'Важное';
      default: return 'Текстовое';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl border border-gray-700">
        {/* Заголовок - фиксированный */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-400" />
            Предпросмотр рассылки
          </h2>
          <button
            onClick={() => setShowPreview(false)}
            className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Содержимое с прокруткой */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Информация о получателях */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Получателей:</span>
              <span className="text-xl font-bold text-white">{previewData?.recipientCount || 0}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400">Фильтр:</span>
              <span className="px-2 py-0.5 bg-gray-700 text-gray-300 rounded-full text-xs">
                {getFilterName()}
              </span>
            </div>
          </div>

          {/* Превью изображения */}
          {previewData?.hasImage && previewData?.imagePreview && (
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-white">Изображение:</span>
              </div>
              <img
                src={previewData.imagePreview}
                alt="Preview"
                className="w-full max-h-64 object-contain rounded-lg border border-gray-700"
              />
            </div>
          )}

          {/* Превью сообщения */}
          {previewData?.text && (
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <MessageSquare className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-white">
                  {previewData.hasImage ? 'Подпись к фото:' : 'Сообщение:'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  previewData.type === 'text' ? 'bg-blue-900/30 text-blue-300 border border-blue-700' :
                  previewData.type === 'notification' ? 'bg-purple-900/30 text-purple-300 border border-purple-700' :
                  'bg-yellow-900/30 text-yellow-300 border border-yellow-700'
                }`}>
                  {getTypeName()}
                </span>
                <span className="text-xs px-2 py-0.5 bg-green-900/30 text-green-300 rounded-full border border-green-700">
                  {previewData.parseMode}
                </span>
              </div>
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 font-mono text-sm whitespace-pre-wrap text-gray-300 max-h-96 overflow-y-auto">
                {previewData.text}
              </div>
            </div>
          )}

          {/* Прогресс отправки */}
          {sending && (
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Прогресс:</span>
                <span className="text-sm font-medium text-white">
                  {sendProgress.sent} / {sendProgress.total}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
                ></div>
              </div>
              {sendProgress.failed.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-red-400 mb-1">
                    Ошибок: {sendProgress.failed.length}
                  </p>
                  <div className="max-h-32 overflow-y-auto">
                    {sendProgress.failed.slice(0, 5).map((fail, i) => (
                      <p key={i} className="text-xs text-gray-400 truncate">
                        {fail.id?.substring(0, 8)}...: {fail.reason}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Кнопки действий - фиксированные внизу */}
        <div className="px-6 py-4 border-t border-gray-700 flex gap-3 flex-shrink-0 bg-gray-900">
          <button
            onClick={sendMessage}
            disabled={sending || !previewData?.recipientCount}
            className={`flex-1 px-4 py-3 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
              sending
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            {sending ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Отправка... {sendProgress.sent}/{sendProgress.total}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Отправить рассылку
              </>
            )}
          </button>
          <button
            onClick={() => setShowPreview(false)}
            disabled={sending}
            className="px-4 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};